import { IndicatorImportModel } from '../IndicatorImport';
import type { QueryClient } from '@tanstack/query-core';
import type {
    IndicatorRef,
    ConcreteIndicator,
    IndicatorImportRef,
} from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import { ToQueryKey } from 'src/lib/config/ThinkAlphaQueryClient';
import type { QueryKey } from 'src/lib/config/ThinkAlphaQueryClient';
import { getIndicatorFromRefQuery } from 'src/queries/dictionary';

export class IndicatorImportRefModel implements IndicatorImportRef, QueryKey {
    #alias: string;
    #id: string;

    get alias(): string {
        return this.#alias;
    }

    [ToQueryKey]() {
        return {
            id: this.#id,
            alias: this.#alias,
        };
    }

    private constructor({ alias, id }: { alias: string; id: string }) {
        this.#alias = alias;
        this.#id = id;
    }

    static fromIndicatorImportRef(viewModel: IndicatorImportRef): IndicatorImportRefModel {
        return new IndicatorImportRefModel(viewModel);
    }

    static fromIndicatorRef(
        ref: IndicatorRef | ConcreteIndicator,
        imports: readonly IndicatorImportRefModel[],
        preferredAlias?: string,
    ) {
        const importModel = imports.find((importModel) => importModel.doesReference(ref));

        if (importModel) {
            return importModel;
        }

        const aliasPrefix = preferredAlias
            ? preferredAlias
            : 'symbol' in ref
              ? ref.symbol
              : `_${ref.id.replaceAll('-', '')}`;
        let alias = aliasPrefix;
        let found;
        let i = 0;
        do {
            found = imports.find((importModel) => importModel.alias === alias);

            if (found && !found.doesReference(ref)) {
                i++;
                alias = `${aliasPrefix}_${i}`;
            }
        } while (found);

        return new IndicatorImportRefModel({
            alias,
            id: ref.id,
        });
    }

    doesReference(b: IndicatorRef) {
        return this.id === b.id;
    }

    static equals(a: IndicatorImportRef, b: IndicatorImportRef) {
        return a.id === b.id && a.alias === b.alias;
    }

    equals(b: IndicatorImportRef) {
        return this.id === b.id && this.alias === b.alias;
    }

    get id(): string {
        return this.#id;
    }

    toContractType(): IndicatorImportRef {
        return {
            alias: this.#alias,
            id: this.#id,
        };
    }

    /**
     * Materialize the ref model
     */
    toIndicator(concreteIndicator: ConcreteIndicator): IndicatorImportModel {
        return IndicatorImportModel.fromIndicatorImport({
            alias: this.#alias,
            ...concreteIndicator,
        });
    }

    async fetchIndicator(queryClient: QueryClient): Promise<IndicatorImportModel> {
        const concreteIndicator = await queryClient.fetchQuery(getIndicatorFromRefQuery(this));
        return this.toIndicator(concreteIndicator);
    }
}
